import axios from 'axios';

// FEATURE FLAG: Set to true when ready to launch Elo ratings publicly
export const ELO_VISIBLE = true;

// Get API base URL from environment or use proxy/default
const getApiUrl = () => {
  // In development, always use localhost (ignore REACT_APP_API_URL)
  if (process.env.NODE_ENV === 'development') {
    console.log('[ApiService] Development mode - using localhost:3002');
    return 'http://localhost:3002';
  }
  // In production, use environment variable or fallback
  const url = process.env.REACT_APP_API_URL || 'https://api.migoyugo.com';
  console.log('[ApiService] Production mode - using:', url);
  return url;
};

// Don't set baseURL in development - use proxy instead
// The proxy in package.json will forward requests from port 3001 to 3002
const API_URL = process.env.NODE_ENV === 'development' ? '' : getApiUrl();
console.log('[ApiService] API_URL initialized to:', API_URL || '(using proxy)', 'NODE_ENV:', process.env.NODE_ENV);

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to set auth token in localStorage
const setAuthToken = (token: string): void => {
  localStorage.setItem('authToken', token);
};

// Helper function to remove auth token from localStorage
const removeAuthToken = (): void => {
  localStorage.removeItem('authToken');
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log the actual URL being used
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    console.log('[ApiService] Making request to:', fullUrl, 'Method:', config.method);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear it
      removeAuthToken();
    }
    return Promise.reject(error);
  }
);

export interface UserStats {
  wins: number;
  losses: number;
  draws: number;
  totalGames: number;
  winRate: number;
  winPercentage: number;
  winStreak: number;
  currentStreak?: number;
  streakType?: 'win' | 'loss' | null;
  elo_fast?: number;
  elo_standard?: number;
  rated_games_fast?: number;
  rated_games_standard?: number;
  elo_rating?: number;
  rated_games_count?: number;
}

export interface GameRecord {
  id: string;
  user_id: string;
  opponent: string;
  result: 'win' | 'loss' | 'draw';
  player_color: 'white' | 'black';
  game_mode: string;
  game_id?: string;
  timestamp: string;
  moves?: any[];
  win_type?: 'igo' | 'wego' | null; // How the game ended: 'igo' for Igo win, 'wego' for Wego win, null for other endings
  game_end_reason?: string | null; // Reason game ended: 'resignation', 'abandon', 'disconnect', 'timeout', etc.
}

export interface LeaderboardEntry {
  id: number;
  username: string;
  wins: number;
  losses: number;
  draws: number;
  total_games: number;
  winRate: number;
  is_online: boolean | number;
  elo_rating?: number;
  rated_games_count?: number;
  elo_fast?: number;
  elo_standard?: number;
  rated_games_fast?: number;
  rated_games_standard?: number;
}

export class ApiService {
  // Authentication methods
  static async signUp(email: string, password: string, username?: string) {
    try {
      const response = await apiClient.post('/api/auth/signup', {
        email,
        password,
        username,
      });
      const token = response.data.token;
      if (token) {
        setAuthToken(token);
      }
      return { success: true, error: null, token, user: response.data.user };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Signup failed',
      };
    }
  }

  static async signIn(email: string, password: string) {
    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password,
      });
      const token = response.data.token;
      if (token) {
        setAuthToken(token);
      }
      return { success: true, error: null, token, user: response.data.user };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed',
      };
    }
  }

  static async signOut() {
    removeAuthToken();
    return { success: true, error: null };
  }

  static async oauthLogin(provider: 'google' | 'facebook', token: string) {
    try {
      const response = await apiClient.post('/api/auth/oauth', {
        provider,
        token,
      });
      const authToken = response.data.token;
      if (authToken) {
        setAuthToken(authToken);
      }
      return {
        success: true,
        error: null,
        token: authToken,
        user: response.data.user,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'OAuth login failed',
      };
    }
  }

  static async checkUsername(username: string) {
    try {
      const response = await apiClient.get(`/api/auth/check-username/${username}`);
      return {
        available: response.data.available,
        error: null,
      };
    } catch (error: any) {
      return {
        available: false,
        error: error.response?.data?.error || error.message || 'Username check failed',
      };
    }
  }

  static async getCurrentUser() {
    try {
      const response = await apiClient.get('/api/auth/profile');
      return { user: response.data.user, error: null };
    } catch (error: any) {
      return {
        user: null,
        error: error.response?.data?.error || error.message || 'Failed to get user',
      };
    }
  }

  // User stats and data methods
  static async getUserStats(): Promise<{ stats: UserStats | null; error: string | null }> {
    try {
      const response = await apiClient.get('/api/auth/stats');
      return { stats: response.data.stats, error: null };
    } catch (error: any) {
      return {
        stats: null,
        error: error.response?.data?.error || error.message || 'Failed to get stats',
      };
    }
  }

  static async getUserStatsById(userId: number): Promise<{ stats: UserStats | null; username: string | null; error: string | null }> {
    try {
      const response = await apiClient.get(`/api/auth/stats/${userId}`);
      return { 
        stats: response.data.stats, 
        username: response.data.username || null,
        error: null 
      };
    } catch (error: any) {
      return {
        stats: null,
        username: null,
        error: error.response?.data?.error || error.message || 'Failed to get stats',
      };
    }
  }

  static async getUserData(userId: string) {
    try {
      // For now, we'll use the profile endpoint which returns current user data
      // If we need to get other users' data, we'll need a new endpoint
      const response = await apiClient.get('/api/auth/profile');
      return response.data.user;
    } catch (error: any) {
      return null;
    }
  }

  static async updateUsername(username: string) {
    try {
      const response = await apiClient.put('/api/auth/profile', {
        username,
      });
      return { success: true, error: null, user: response.data.user };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update username',
      };
    }
  }

  // Game-related methods
  static async recordGame(
    opponent: string,
    result: 'win' | 'loss' | 'draw',
    playerColor: 'white' | 'black',
    gameMode: string,
    moves?: Array<{ row: number; col: number; player: 'white' | 'black'; yugos?: number }>
  ) {
    try {
      await apiClient.post('/api/auth/record-game', {
        opponent,
        result,
        playerColor,
        gameMode,
        moves: moves || null, // Send moves array for replay functionality
      });
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to record game',
      };
    }
  }

  static async getGameHistory(limit?: number): Promise<{ games: GameRecord[]; error: string | null }> {
    try {
      const params = limit ? { limit } : {};
      const response = await apiClient.get('/api/auth/game-history', { params });
      // Backend returns 'history', but we normalize it to 'games' for consistency
      return { games: response.data.history || response.data.games || [], error: null };
    } catch (error: any) {
      return {
        games: [],
        error: error.response?.data?.error || error.message || 'Failed to get game history',
      };
    }
  }

  static async getGameHistoryById(userId: number, limit?: number): Promise<{ games: GameRecord[]; error: string | null }> {
    try {
      const params = limit ? { limit } : {};
      const response = await apiClient.get(`/api/auth/game-history/${userId}`, { params });
      // Backend returns 'history', but we normalize it to 'games' for consistency
      return { games: response.data.history || response.data.games || [], error: null };
    } catch (error: any) {
      return {
        games: [],
        error: error.response?.data?.error || error.message || 'Failed to get game history',
      };
    }
  }

  // Get game replay data
  static async getGameReplay(gameId: string): Promise<{ game: any; error: string | null }> {
    try {
      const response = await apiClient.get(`/api/auth/game/${gameId}/replay`);
      return { game: response.data.game, error: null };
    } catch (error: any) {
      return {
        game: null,
        error: error.response?.data?.error || error.message || 'Failed to get game replay',
      };
    }
  }

  // Email verification
  static async verifyEmail(token: string): Promise<{ success: boolean; error: string | null; user?: any }> {
    try {
      const response = await apiClient.get(`/api/auth/verify-email?token=${token}`);
      return { success: true, error: null, user: response.data.user };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to verify email',
      };
    }
  }

  static async resendVerificationEmail(): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await apiClient.post('/api/auth/resend-verification');
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to resend verification email',
      };
    }
  }

  // Password reset methods
  static async requestPasswordReset(email: string): Promise<{ success: boolean; message: string; error: string | null }> {
    try {
      const response = await apiClient.post('/api/auth/forgot-password', { email });
      return { 
        success: response.data.success || true, 
        message: response.data.message || 'If an account with that email exists, a password reset link has been sent.',
        error: null 
      };
    } catch (error: any) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.error || error.message || 'Failed to send password reset email',
      };
    }
  }

  static async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string; error: string | null }> {
    try {
      const response = await apiClient.post('/api/auth/reset-password', { token, newPassword });
      return { 
        success: true, 
        message: response.data.message || 'Password has been reset successfully.',
        error: null 
      };
    } catch (error: any) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.error || error.message || 'Failed to reset password',
      };
    }
  }

  static async getLeaderboard(period: string = 'alltime'): Promise<{ leaderboard: LeaderboardEntry[]; error: string | null }> {
    try {
      const response = await apiClient.get('/api/auth/leaderboard', {
        params: { period },
      });
      return { leaderboard: response.data.leaderboard || [], error: null };
    } catch (error: any) {
      return {
        leaderboard: [],
        error: error.response?.data?.error || error.message || 'Failed to get leaderboard',
      };
    }
  }

  // Online status methods
  static async updateOnlineStatus(isOnline: boolean) {
    try {
      await apiClient.post('/api/auth/update-online-status', {
        isOnline,
      });
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update online status',
      };
    }
  }

  // Player search methods
  static async getPlayers(search?: string, onlineOnly?: boolean) {
    try {
      const params: any = {};
      if (search) params.search = search;
      if (onlineOnly) params.onlineOnly = 'true';
      
      const response = await apiClient.get('/api/auth/players', { params });
      return { players: response.data.players || [], error: null };
    } catch (error: any) {
      return {
        players: [],
        error: error.response?.data?.error || error.message || 'Failed to get players',
      };
    }
  }

  static async getPlayerProfile(username: string) {
    try {
      const response = await apiClient.get(`/api/auth/players/${username}`);
      return { player: response.data.player, recentGames: response.data.recentGames || [], error: null };
    } catch (error: any) {
      return {
        player: null,
        recentGames: [],
        error: error.response?.data?.error || error.message || 'Failed to get player profile',
      };
    }
  }

  // Challenge methods
  static async sendChallenge(challengedId: number, timerSettings?: { minutesPerPlayer: number; incrementSeconds: number }, isRated?: boolean) {
    try {
      const response = await apiClient.post('/api/auth/challenges', {
        challengedId,
        timerSettings: timerSettings || { minutesPerPlayer: 10, incrementSeconds: 0 },
        isRated: isRated !== undefined ? isRated : true
      });
      return { success: true, challenge: response.data.challenge, error: null, gameId: null };
    } catch (error: any) {
      return {
        success: false,
        challenge: null,
        error: error.response?.data?.error || error.message || 'Failed to send challenge',
        gameId: error.response?.data?.gameId || null,
      };
    }
  }

  static async getChallenges() {
    try {
      const response = await apiClient.get('/api/auth/challenges');
      return { challenges: response.data.challenges || [], error: null };
    } catch (error: any) {
      return {
        challenges: [],
        error: error.response?.data?.error || error.message || 'Failed to get challenges',
      };
    }
  }

  static async acceptChallenge(challengeId: number) {
    try {
      await apiClient.post(`/api/auth/challenges/${challengeId}/accept`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to accept challenge',
      };
    }
  }

  static async declineChallenge(challengeId: number) {
    try {
      await apiClient.post(`/api/auth/challenges/${challengeId}/decline`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to decline challenge',
      };
    }
  }


  // Block system
  static async blockUser(blockedId: number) {
    try {
      const response = await apiClient.post('/api/auth/blocks', {
        blockedId,
      });
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to block user',
      };
    }
  }

  static async unblockUser(blockedId: number) {
    try {
      await apiClient.delete(`/api/auth/blocks/${blockedId}`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to unblock user',
      };
    }
  }

  static async getBlockedUsers() {
    try {
      const response = await apiClient.get('/api/auth/blocks');
      return { 
        success: true, 
        blocks: response.data.blocks || [],
        error: null 
      };
    } catch (error: any) {
      return {
        success: false,
        blocks: [],
        error: error.response?.data?.error || error.message || 'Failed to get blocked users',
      };
    }
  }

  static async getUserPreferences(): Promise<{ preferences: { theme: string | null; customColors: any; gameSoundsEnabled?: boolean; notificationsEnabled?: boolean; soundEnabled?: boolean; dmPrivacy?: string } | null; error: string | null }> {
    try {
      const response = await apiClient.get('/api/auth/preferences');
      return { preferences: response.data.preferences, error: null };
    } catch (error: any) {
      return { preferences: null, error: error.response?.data?.error || error.message || 'Failed to get preferences' };
    }
  }

  static async saveUserPreferences(preferences: { theme?: string | null; customColors?: any; gameSoundsEnabled?: boolean; notificationsEnabled?: boolean; soundEnabled?: boolean; dmPrivacy?: string }): Promise<{ success: boolean; error: string | null }> {
    try {
      await apiClient.post('/api/auth/preferences', preferences);
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to save preferences' };
    }
  }

  // Saved positions methods (members only)
  static async getSavedPositions(): Promise<{ positions: Array<{ id: number; name: string; position_string: string; created_at: string; updated_at: string }>; error: string | null }> {
    try {
      const response = await apiClient.get('/api/auth/saved-positions');
      return { positions: response.data.positions || [], error: null };
    } catch (error: any) {
      return {
        positions: [],
        error: error.response?.data?.error || error.message || 'Failed to get saved positions',
      };
    }
  }

  static async savePosition(name: string, positionString: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await apiClient.post('/api/auth/saved-positions', { name, positionString });
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to save position',
      };
    }
  }

  static async getSavedPositionById(positionId: number): Promise<{ position: { id: number; name: string; position_string: string; created_at: string; updated_at: string } | null; error: string | null }> {
    try {
      const response = await apiClient.get(`/api/auth/saved-positions/${positionId}`);
      return { position: response.data.position, error: null };
    } catch (error: any) {
      return {
        position: null,
        error: error.response?.data?.error || error.message || 'Failed to get saved position',
      };
    }
  }

  static async deleteSavedPosition(positionId: number): Promise<{ success: boolean; error: string | null }> {
    try {
      await apiClient.delete(`/api/auth/saved-positions/${positionId}`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete position',
      };
    }
  }

  static async setInGame(inGame: boolean, gameId?: string) {
    try {
      await apiClient.post('/api/auth/update-in-game', {
        inGame,
        gameId,
      });
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update in-game status',
      };
    }
  }

  // Spectator methods
  static async getActiveGames(): Promise<{ games: any[]; error: string | null }> {
    try {
      const response = await apiClient.get('/api/games/active');
      return { games: response.data.games || [], error: null };
    } catch (error: any) {
      return {
        games: [],
        error: error.response?.data?.error || error.message || 'Failed to get active games',
      };
    }
  }

  // Tournament methods
  static async getActiveTournaments(): Promise<{ tournaments: any[]; error: string | null }> {
    try {
      const response = await apiClient.get('/api/tournaments');
      return { tournaments: response.data.tournaments || [], error: null };
    } catch (error: any) {
      return { tournaments: [], error: error.response?.data?.error || error.message || 'Failed to get tournaments' };
    }
  }

  static async getTournament(id: number): Promise<{ tournament: any; participants: any[]; matches: any[]; roundSettings: any[]; error: string | null }> {
    try {
      const response = await apiClient.get(`/api/tournaments/${id}`);
      return {
        tournament: response.data.tournament,
        participants: response.data.participants || [],
        matches: response.data.matches || [],
        roundSettings: response.data.roundSettings || [],
        error: null
      };
    } catch (error: any) {
      return { tournament: null, participants: [], matches: [], roundSettings: [], error: error.response?.data?.error || error.message || 'Failed to get tournament' };
    }
  }

  static async signUpForTournament(id: number): Promise<{ error: string | null }> {
    try {
      await apiClient.post(`/api/tournaments/${id}/signup`);
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to sign up' };
    }
  }

  static async withdrawFromTournament(id: number): Promise<{ error: string | null }> {
    try {
      await apiClient.post(`/api/tournaments/${id}/withdraw`);
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to withdraw' };
    }
  }

  static async trackVisitorEvent(
    visitorId: string,
    eventType: 'visit' | 'puzzle_start' | 'puzzle_solve' | 'bot_start' | 'bot_finish',
    detail?: string
  ): Promise<void> {
    await apiClient.post('/api/analytics/event', { visitorId, eventType, detail });
  }

  // Token management
  static getToken(): string | null {
    return getAuthToken();
  }

  static isAuthenticated(): boolean {
    return getAuthToken() !== null;
  }
}


